import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, MapPin, MoreHorizontal, Edit, Trash2, Home, Calendar, Key, Map as MapIcon, List } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';
import PropertyMap from '../../components/PropertyMap';

const PropertiesPage = () => {
  const { token, API } = useAuth();
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [formData, setFormData] = useState({
    customer_id: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    latitude: '',
    longitude: '',
    property_size: '',
    size_unit: 'sqft',
    service_instructions: '',
    access_notes: '',
    irrigation_details: '',
    gate_code: '',
    preferred_service_days: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, customersRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProperties(propertiesRes.data.properties);
      setCustomers(customersRes.data.customers);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.address) {
      toast.error('Customer and address are required');
      return;
    }

    const submitData = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      property_size: formData.property_size ? parseFloat(formData.property_size) : null,
    };

    try {
      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await axios.delete(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const openEditDialog = (property) => {
    setEditingProperty(property);
    setFormData({
      customer_id: property.customer_id,
      address: property.address,
      city: property.city || '',
      state: property.state || '',
      zip_code: property.zip_code || '',
      latitude: property.latitude?.toString() || '',
      longitude: property.longitude?.toString() || '',
      property_size: property.property_size?.toString() || '',
      size_unit: property.size_unit || 'sqft',
      service_instructions: property.service_instructions || '',
      access_notes: property.access_notes || '',
      irrigation_details: property.irrigation_details || '',
      gate_code: property.gate_code || '',
      preferred_service_days: property.preferred_service_days || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProperty(null);
    setFormData({
      customer_id: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      latitude: '',
      longitude: '',
      property_size: '',
      size_unit: 'sqft',
      service_instructions: '',
      access_notes: '',
      irrigation_details: '',
      gate_code: '',
      preferred_service_days: []
    });
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const filteredProperties = properties.filter(p =>
    p.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getCustomerName(p.customer_id).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <ClientLayout title="Properties">
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-properties-input"
              />
            </div>
            <div className="flex gap-2">
              {/* View Mode Toggle */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-primary rounded-none' : 'rounded-none'}
                  data-testid="view-list-btn"
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('map')}
                  className={viewMode === 'map' ? 'bg-primary rounded-none' : 'rounded-none'}
                  data-testid="view-map-btn"
                >
                  <MapIcon className="h-4 w-4" />
                </Button>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90" data-testid="add-property-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Property
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Customer *</Label>
                    <Select 
                      value={formData.customer_id} 
                      onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                    >
                      <SelectTrigger data-testid="property-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Address *</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main Street"
                      data-testid="property-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        placeholder="40.7128"
                      />
                    </div>
                    <div>
                      <Label>Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        placeholder="-74.0060"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Property Size</Label>
                      <Input
                        type="number"
                        value={formData.property_size}
                        onChange={(e) => setFormData({ ...formData, property_size: e.target.value })}
                        placeholder="5000"
                      />
                    </div>
                    <div>
                      <Label>Size Unit</Label>
                      <Select 
                        value={formData.size_unit} 
                        onValueChange={(value) => setFormData({ ...formData, size_unit: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sqft">Square Feet</SelectItem>
                          <SelectItem value="acres">Acres</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Service Instructions</Label>
                    <Textarea
                      value={formData.service_instructions}
                      onChange={(e) => setFormData({ ...formData, service_instructions: e.target.value })}
                      placeholder="Mow front and back lawn, trim hedges..."
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Access Notes</Label>
                      <Textarea
                        value={formData.access_notes}
                        onChange={(e) => setFormData({ ...formData, access_notes: e.target.value })}
                        placeholder="Gate on left side, dog in backyard..."
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Gate Code</Label>
                      <Input
                        value={formData.gate_code}
                        onChange={(e) => setFormData({ ...formData, gate_code: e.target.value })}
                        placeholder="1234#"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Irrigation Details</Label>
                    <Textarea
                      value={formData.irrigation_details}
                      onChange={(e) => setFormData({ ...formData, irrigation_details: e.target.value })}
                      placeholder="Sprinkler system, water timer location..."
                      rows={2}
                    />
                  </div>

                  <div>
                    <Label>Preferred Service Days</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {daysOfWeek.map((day) => (
                        <Button
                          key={day}
                          type="button"
                          variant={formData.preferred_service_days.includes(day) ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            const days = formData.preferred_service_days.includes(day)
                              ? formData.preferred_service_days.filter(d => d !== day)
                              : [...formData.preferred_service_days, day];
                            setFormData({ ...formData, preferred_service_days: days });
                          }}
                          className={formData.preferred_service_days.includes(day) ? 'bg-primary' : ''}
                        >
                          {day.slice(0, 3)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSubmit} data-testid="save-property-btn">
                    {editingProperty ? 'Save Changes' : 'Add Property'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No properties found</p>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                Add Your First Property
              </Button>
            </div>
          ) : viewMode === 'map' ? (
            <div className="space-y-4">
              <PropertyMap properties={filteredProperties} customers={customers} />
              <p className="text-sm text-gray-500 text-center">
                {filteredProperties.filter(p => p.latitude && p.longitude).length} of {filteredProperties.length} properties shown on map
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProperties.map((property) => (
                <div 
                  key={property.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  data-testid={`property-${property.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Home className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{property.address}</p>
                        <p className="text-xs text-gray-500">{getCustomerName(property.customer_id)}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(property)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(property.id)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {property.city && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span>{property.city}, {property.state} {property.zip_code}</span>
                      </div>
                    )}
                    {property.property_size && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Home className="h-4 w-4 text-gray-400" />
                        <span>{property.property_size.toLocaleString()} {property.size_unit}</span>
                      </div>
                    )}
                    {property.gate_code && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Key className="h-4 w-4 text-gray-400" />
                        <span>Gate: {property.gate_code}</span>
                      </div>
                    )}
                    {property.preferred_service_days && property.preferred_service_days.length > 0 && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>{property.preferred_service_days.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
};

export default PropertiesPage;
